// frontend_react/src/components/AdminPanel/CashRegisterDashboardPanel.jsx

import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import {
  getCashRegisterDashboard,
  getCashRegisterXReport,
  getCurrentCashSession,
  openGlobalCashSession,
  closeGlobalCashSession,
  getCashSessionHistory,
  getZReportDetail,
  getZReportPdfUrl,
  sendZReportByEmail,
  sendZReportToAccounting,
} from "../../services/cashRegisterApi";
import PdfEmailModal from "../common/PdfEmailModal";
import { useAdminScope } from "../../context/AdminScopeContext";
import { AdminButton } from "../../pages/admin/action_engine";
import {
  AdminNavigationTab,
  AdminNavigationTabs,
} from "../../pages/admin/navigation_engine";

import {
  AdminFormSection,
  AdminSectionBadge,
  AdminSectionHeader,
} from "../../pages/admin/section_engine";

export default function CashRegisterDashboardPanel({
  autoOpenX = false,
  autoOpenZ = false,
  initialView = null,
  hideSubNav = false,
}) {
  const [activeView, setActiveView] = useState(
    initialView || (autoOpenX ? "x" : autoOpenZ ? "z" : "overview")
  );
  const { selectedCompanyUuid } = useAdminScope();
  const [dashboard, setDashboard] = useState(null);
  const [xReport, setXReport] = useState(null);
  const [zReport, setZReport] = useState(null);

  const [loading, setLoading] = useState(true);
  const [localMessage, setLocalMessage] = useState("");
  const [isError, setIsError] = useState(false);

  const [cashSessionUuid, setCashSessionUuid] = useState("");
  const [isCashOpen, setIsCashOpen] = useState(false);

  const [openingCash, setOpeningCash] = useState("100");
  const [countedCash, setCountedCash] = useState("100");
  const [bankDeposit, setBankDeposit] = useState("0");
  const [closureComment, setClosureComment] = useState("RAS");

  const [zHistory, setZHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const loadDashboard = useCallback(async () => {
    try {
      setLoading(true);
      setLocalMessage("");
      setIsError(false);

      const current = await getCurrentCashSession({
        companyUuid: selectedCompanyUuid,
      });

      if (current?.is_open) {
        setIsCashOpen(true);
        setCashSessionUuid(current.cash_session_uuid);
        setOpeningCash(String(current.opening_cash ?? "0"));
      } else {
        setIsCashOpen(false);
        setCashSessionUuid("");
      }

      const data = await getCashRegisterDashboard({
        companyUuid: selectedCompanyUuid,
      });
      setDashboard(data);
    } catch (error) {
      console.error(error);
      setLocalMessage("Impossible de charger le dashboard caisse.");
      setIsError(true);
    } finally {
      setLoading(false);
    }
  }, [selectedCompanyUuid]);

  const loadXReport = useCallback(async () => {
    try {
      setLocalMessage("");
      setIsError(false);

      const data = await getCashRegisterXReport({
        companyUuid: selectedCompanyUuid,
      });
      setXReport(data);
    } catch (error) {
      console.error(error);
      setLocalMessage("Impossible de charger le X de caisse.");
      setIsError(true);
    }
  }, [selectedCompanyUuid]);

  const handleOpenCashSession = async () => {
    try {
      setLocalMessage("");
      setIsError(false);

      await openGlobalCashSession(
        {
          opening_cash: Number(openingCash || 0),
        },
        {
          companyUuid: selectedCompanyUuid,
        }
      );

      await loadDashboard();
      setLocalMessage("Caisse globale ouverte.");
      setIsError(false);
    } catch (error) {
      const response = error?.response?.data;

      if (response?.cash_session_uuid) {
        await loadDashboard();
        setLocalMessage("Une caisse est déjà ouverte. Session récupérée.");
        setIsError(false);
        return;
      }

      setLocalMessage(response?.error || "Impossible d’ouvrir la caisse.");
      setIsError(true);
    }
  };

  const handleCloseCashSession = async () => {
    try {
      setLocalMessage("");
      setIsError(false);

      if (!cashSessionUuid) {
        setLocalMessage("Aucune session de caisse ouverte à clôturer.");
        setIsError(true);
        return;
      }

      const data = await closeGlobalCashSession(
        cashSessionUuid,
        {
          counted_cash: Number(countedCash || 0),
          bank_deposit: Number(bankDeposit || 0),
          closure_comment: closureComment,
        },
        {
          companyUuid: selectedCompanyUuid,
        }
      );

      setZReport(data);
      setCashSessionUuid("");
      setIsCashOpen(false);

      await loadDashboard();

      setLocalMessage("Clôture Z réalisée avec succès.");
      setIsError(false);
    } catch (error) {
      const response = error?.response?.data;
      setLocalMessage(response?.error || "Impossible de clôturer la caisse.");
      setIsError(true);
    }
  };

  const loadZHistory = async () => {
    try {
      setHistoryLoading(true);
      setLocalMessage("");
      setIsError(false);

      const data = await getCashSessionHistory({
        companyUuid: selectedCompanyUuid,
      });
      setZHistory(data.sessions ?? []);
    } catch (error) {
      console.error(error);
      setLocalMessage("Impossible de charger l’historique Z.");
      setIsError(true);
    } finally {
      setHistoryLoading(false);
    }
  };

  const [selectedZReport, setSelectedZReport] = useState(null);
  const [emailModalSessionUuid, setEmailModalSessionUuid] = useState(null);
  const [emailTo, setEmailTo] = useState("");
  const [emailCc, setEmailCc] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");

  const handleViewZReport = async (cashSessionUuid) => {
    try {
      setLocalMessage("");
      setIsError(false);

      const data = await getZReportDetail(cashSessionUuid, {
        companyUuid: selectedCompanyUuid,
      });
      setSelectedZReport(data);
    } catch (error) {
      console.error(error);
      setLocalMessage("Impossible de charger le détail du Z.");
      setIsError(true);
    }
  };

  const handleOpenZPdf = (cashSessionUuid) => {
    window.open(
      getZReportPdfUrl(cashSessionUuid, selectedCompanyUuid),
      "_blank",
      "noopener,noreferrer"
    );
  };

  const openZEmailModal = async (cashSessionUuid) => {
    const today = new Date().toISOString().slice(0, 10);

    setEmailModalSessionUuid(cashSessionUuid);
    setEmailTo("");
    setEmailCc("");

    try {
      const detail = await getZReportDetail(cashSessionUuid, {
        companyUuid: selectedCompanyUuid,
      });

      const companyName =
        detail?.company?.name ||
        detail?.company_name ||
        detail?.user_company_name ||
        "Queen's 360";

      setEmailSubject(`${companyName}_Rapport Z_${today}`);

      setEmailBody(
        `Bonjour,\n\n` +
          `Veuillez trouver ci-joint le rapport Z de clôture de caisse.\n\n` +
          `Cordialement,\n\n` +
          `L'équipe ${companyName}\n\n`
      );
    } catch (error) {
      console.error(error);

      setEmailSubject(`Queen's 360_Rapport Z_${today}`);

      setEmailBody(
        "Bonjour,\n\n" +
          "Veuillez trouver ci-joint le rapport Z de clôture de caisse.\n\n" +
          "Cordialement,\n\n" +
          "La direction\n\n"
      );
    }
  };

  const handleSendZAccounting = async (cashSessionUuid) => {
    try {
      setLocalMessage("");
      setIsError(false);

      const result = await sendZReportToAccounting(cashSessionUuid, {
        companyUuid: selectedCompanyUuid,
      });
      await loadZHistory();

      if (selectedZReport) {
        const refreshed = await getZReportDetail(cashSessionUuid, {
          companyUuid: selectedCompanyUuid,
        });
        setSelectedZReport(refreshed);
      }

      const entriesCount =
        result?.accounting_result?.entries_count ??
        result?.entries_count ??
        0;

      const created =
        result?.accounting_result?.created ??
        result?.created ??
        false;

      setLocalMessage(
        created
          ? `Z transmis en comptabilité : ${entriesCount} écriture(s) générée(s).`
          : `Z déjà transmis en comptabilité : ${entriesCount} écriture(s) existante(s).`
      );
    } catch (error) {
      console.error(error);
      setLocalMessage("Impossible d’envoyer le Z en comptabilité.");
      setIsError(true);
    }
  };

  const handleViewChange = async (view) => {
    setActiveView(view);

    if (view === "x") {
      await loadXReport();
    }

    if (view === "history") {
      await loadZHistory();
    }
  };

  useEffect(() => {
    setDashboard(null);
    setXReport(null);
    setZReport(null);
    setZHistory([]);
    setSelectedZReport(null);
    setEmailModalSessionUuid(null);
    setCashSessionUuid("");
    setIsCashOpen(false);
    loadDashboard();
  }, [selectedCompanyUuid, loadDashboard]);

  useEffect(() => {
    if (autoOpenX) {
      setActiveView("x");
      loadXReport();
    }
  }, [autoOpenX, selectedCompanyUuid, loadXReport]);

  useEffect(() => {
    if (autoOpenZ) {
      setActiveView("z");
      setLocalMessage("Module Clôture Z prêt. Saisis les montants puis lance la clôture.");
      setIsError(false);
    }
  }, [autoOpenZ, selectedCompanyUuid]);

  const today = dashboard?.today || {};
  const week = dashboard?.week || {};
  const month = dashboard?.month || {};
  const payments = dashboard?.payments || {};

  return (
    <PanelWrapper>
      {localMessage && (
        <PanelMessage $error={isError}>
          {localMessage}
        </PanelMessage>
      )}

      <HeaderCard>
        <div>
          <AdminSectionBadge>Caisse</AdminSectionBadge>
          <h4>Gestion de caisse</h4>
          <p>
            Choisis une vue : suivi caisse, X, clôture Z, historique ou tickets.
          </p>
        </div>

        <CashState $open={isCashOpen}>
          <span />
          {isCashOpen ? "Caisse ouverte" : "Caisse fermée"}
        </CashState>
      </HeaderCard>

      {!hideSubNav && (
        <AdminNavigationTabs aria-label="Navigation de caisse">
          <AdminNavigationTab
            active={activeView === "overview"}
            onClick={() => handleViewChange("overview")}
          >
            Vue caisse
          </AdminNavigationTab>

          <AdminNavigationTab
            active={activeView === "x"}
            onClick={() => handleViewChange("x")}
          >
            X de caisse
          </AdminNavigationTab>

          <AdminNavigationTab
            active={activeView === "z"}
            onClick={() => handleViewChange("z")}
          >
            Clôture Z
          </AdminNavigationTab>

          <AdminNavigationTab
            active={activeView === "history"}
            onClick={() => handleViewChange("history")}
          >
            Historique Z
          </AdminNavigationTab>

          <AdminNavigationTab
            active={activeView === "tickets"}
            onClick={() => handleViewChange("tickets")}
          >
            Tickets
          </AdminNavigationTab>
        </AdminNavigationTabs>
      )}

      {activeView === "overview" && (
        <>
          <AdminFormSection>
            <AdminSectionHeader>
              <AdminSectionBadge>Session</AdminSectionBadge>
              <h4>État de la caisse</h4>
              <p>
                Session active : {cashSessionUuid || "Aucune session ouverte"}
              </p>
            </AdminSectionHeader>

            <ActionGrid>
              <AdminButton type="button" onClick={loadDashboard} disabled={loading}>
                {loading ? "Chargement..." : "Actualiser"}
              </AdminButton>

              <AdminButton
                type="button"
                onClick={handleOpenCashSession}
                disabled={isCashOpen}
              >
                Ouvrir caisse globale
              </AdminButton>
            </ActionGrid>
          </AdminFormSection>

          <AdminFormSection>
            <AdminSectionHeader>
              <AdminSectionBadge>Aujourd’hui</AdminSectionBadge>
              <h4>Indicateurs du jour</h4>
            </AdminSectionHeader>

            <KPIGrid>
              <KPICard>
                <KPILabel>CA TTC</KPILabel>
                <KPIValue>{formatEUR(today.total_ttc)}</KPIValue>
              </KPICard>

              <KPICard>
                <KPILabel>CA HT</KPILabel>
                <KPIValue>{formatEUR(today.total_ht)}</KPIValue>
              </KPICard>

              <KPICard>
                <KPILabel>TVA</KPILabel>
                <KPIValue>{formatEUR(today.vat)}</KPIValue>
              </KPICard>

              <KPICard>
                <KPILabel>Tickets</KPILabel>
                <KPIValue>{formatNumber(today.tickets_count)}</KPIValue>
              </KPICard>

              <KPICard>
                <KPILabel>Panier moyen</KPILabel>
                <KPIValue>{formatEUR(today.average_ticket)}</KPIValue>
              </KPICard>
            </KPIGrid>
          </AdminFormSection>

          <AdminFormSection>
            <AdminSectionHeader>
              <AdminSectionBadge>Semaine</AdminSectionBadge>
              <h4>Indicateurs hebdomadaires</h4>
            </AdminSectionHeader>

            <KPIGrid>
              <KPICard>
                <KPILabel>CA TTC semaine</KPILabel>
                <KPIValue>{formatEUR(week.total_ttc)}</KPIValue>
              </KPICard>

              <KPICard>
                <KPILabel>Tickets semaine</KPILabel>
                <KPIValue>{formatNumber(week.tickets_count)}</KPIValue>
              </KPICard>

              <KPICard>
                <KPILabel>Panier moyen semaine</KPILabel>
                <KPIValue>{formatEUR(week.average_ticket)}</KPIValue>
              </KPICard>
            </KPIGrid>
          </AdminFormSection>

          <AdminFormSection>
            <AdminSectionHeader>
              <AdminSectionBadge>Mois</AdminSectionBadge>
              <h4>Indicateurs mensuels</h4>
            </AdminSectionHeader>

            <KPIGrid>
              <KPICard>
                <KPILabel>CA TTC mois</KPILabel>
                <KPIValue>{formatEUR(month.total_ttc)}</KPIValue>
              </KPICard>

              <KPICard>
                <KPILabel>Tickets mois</KPILabel>
                <KPIValue>{formatNumber(month.tickets_count)}</KPIValue>
              </KPICard>

              <KPICard>
                <KPILabel>Panier moyen mois</KPILabel>
                <KPIValue>{formatEUR(month.average_ticket)}</KPIValue>
              </KPICard>
            </KPIGrid>
          </AdminFormSection>

          <PaymentSection payments={payments} />

          {dashboard?.generated_at && (
            <InlineInfo>
              Dernière génération :{" "}
              {new Date(dashboard.generated_at).toLocaleString("fr-FR")}
            </InlineInfo>
          )}
        </>
      )}

      {activeView === "x" && (
        <AdminFormSection>
          <AdminSectionHeader>
            <AdminSectionBadge>X de caisse</AdminSectionBadge>
            <h4>Situation instantanée</h4>
            <p>
              Le X permet de consulter la situation de caisse sans clôturer la session.
            </p>
          </AdminSectionHeader>

          <AdminButton type="button" onClick={loadXReport}>
            Générer / Actualiser le X
          </AdminButton>

          {xReport && (
            <>
              <InlineInfo>
                Rapport généré le{" "}
                {new Date(xReport.generated_at).toLocaleString("fr-FR")}
              </InlineInfo>

              <KPIGrid>
                <KPICard>
                  <KPILabel>CA TTC</KPILabel>
                  <KPIValue>{formatEUR(xReport.summary?.total_ttc)}</KPIValue>
                </KPICard>

                <KPICard>
                  <KPILabel>CA HT</KPILabel>
                  <KPIValue>{formatEUR(xReport.summary?.total_ht)}</KPIValue>
                </KPICard>

                <KPICard>
                  <KPILabel>TVA</KPILabel>
                  <KPIValue>{formatEUR(xReport.summary?.vat)}</KPIValue>
                </KPICard>

                <KPICard>
                  <KPILabel>Tickets</KPILabel>
                  <KPIValue>{formatNumber(xReport.summary?.tickets_count)}</KPIValue>
                </KPICard>

                <KPICard>
                  <KPILabel>Panier moyen</KPILabel>
                  <KPIValue>{formatEUR(xReport.summary?.average_ticket)}</KPIValue>
                </KPICard>
              </KPIGrid>

              <PaymentSection payments={xReport.payments || {}} />
            </>
          )}
        </AdminFormSection>
      )}

      {activeView === "z" && (
        <AdminFormSection>
          <AdminSectionHeader>
            <AdminSectionBadge>Clôture Z</AdminSectionBadge>
            <h4>Clôture globale de caisse</h4>
            <p>
              Le Z clôture définitivement la session active et génère une signature NF525.
            </p>
          </AdminSectionHeader>

          <InlineInfo>
            Session active : {cashSessionUuid || "Aucune session ouverte"}
          </InlineInfo>

          <FieldsGrid>
            {!isCashOpen && (
              <Field>
                <label>Fond de caisse ouverture</label>
                <input
                  type="number"
                  step="0.01"
                  value={openingCash}
                  onChange={(event) => setOpeningCash(event.target.value)}
                />
              </Field>
            )}

            <Field>
              <label>Espèces constatées</label>
              <input
                type="number"
                step="0.01"
                value={countedCash}
                onChange={(event) => setCountedCash(event.target.value)}
              />
            </Field>

            <Field>
              <label>Dépôt banque</label>
              <input
                type="number"
                step="0.01"
                value={bankDeposit}
                onChange={(event) => setBankDeposit(event.target.value)}
              />
            </Field>

            <Field $full>
              <label>Commentaire clôture</label>
              <input
                type="text"
                value={closureComment}
                onChange={(event) => setClosureComment(event.target.value)}
              />
            </Field>
          </FieldsGrid>

          <ActionGrid>
            {!isCashOpen && (
              <AdminButton type="button" onClick={handleOpenCashSession}>
                Ouvrir caisse globale
              </AdminButton>
            )}

            <AdminButton variant="danger"
              type="button"
              onClick={handleCloseCashSession}
              disabled={!isCashOpen}
            >
              Clôturer Z
            </AdminButton>
          </ActionGrid>

          {zReport && (
            <ResultBlock>
              <AdminSectionHeader>
                <AdminSectionBadge>Z certifié</AdminSectionBadge>
                <h4>Clôture réalisée</h4>
                <p>
                  Rapport Z généré le{" "}
                  {new Date(zReport.closed_at).toLocaleString("fr-FR")}
                </p>
              </AdminSectionHeader>

              <KPIGrid>
                <KPICard>
                  <KPILabel>CA TTC</KPILabel>
                  <KPIValue>{formatEUR(zReport.summary?.total_ttc)}</KPIValue>
                </KPICard>

                <KPICard>
                  <KPILabel>CA HT</KPILabel>
                  <KPIValue>{formatEUR(zReport.summary?.total_ht)}</KPIValue>
                </KPICard>

                <KPICard>
                  <KPILabel>TVA</KPILabel>
                  <KPIValue>{formatEUR(zReport.summary?.total_vat)}</KPIValue>
                </KPICard>

                <KPICard>
                  <KPILabel>Tickets</KPILabel>
                  <KPIValue>{formatNumber(zReport.summary?.tickets_count)}</KPIValue>
                </KPICard>

                <KPICard>
                  <KPILabel>Écart caisse</KPILabel>
                  <KPIValue>{formatEUR(zReport.cash_control?.cash_difference)}</KPIValue>
                </KPICard>
              </KPIGrid>

              <PaymentSection payments={zReport.payments || {}} />

              <InlineInfo>
                Signature NF525 : {zReport.signature_hash}
              </InlineInfo>
            </ResultBlock>
          )}
        </AdminFormSection>
      )}

      {activeView === "history" && (
        <AdminFormSection>
          <AdminSectionHeader>
            <AdminSectionBadge>Historique Z</AdminSectionBadge>
            <h4>Historique des clôtures</h4>
            <p>
              Liste des sessions de caisse ouvertes et clôturées.
            </p>
          </AdminSectionHeader>

          <AdminButton type="button" onClick={loadZHistory} disabled={historyLoading}>
            {historyLoading ? "Chargement..." : "Actualiser l’historique"}
          </AdminButton>

          {zHistory.length === 0 ? (
            <PlaceholderBox>
              Aucun historique Z disponible.
            </PlaceholderBox>
          ) : (
            <ZHistoryTableWrapper>
              <ZHistoryTable>
                <thead>
                  <tr>
                    <th>Ouverture</th>
                    <th>Clôture</th>
                    <th>Dépôt</th>
                    <th>Écart</th>
                    <th>Envoyer PDF</th>
                    <th>Compta</th>
                    <th>Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {zHistory.map((session) => (
                    <tr key={session.cash_session_uuid}>
                      <td>{formatDate(session.started_at)}</td>
                      <td>{formatDate(session.ended_at)}</td>
                      <td>{formatEUR(session.bank_deposit)}</td>
                      <td>{formatEUR(session.cash_difference)}</td>

                      <td>{session.email_sent ? "Envoyé" : "Non envoyé"}</td>

                      <td>{session.accounting_sent ? "Envoyé compta" : "Non envoyé"}</td>

                      <td>
                        <AdminButton size="small"
                          type="button"
                          onClick={() => handleViewZReport(session.cash_session_uuid)}
                        >
                          Voir
                        </AdminButton>

                        <AdminButton size="small"
                          type="button"
                          onClick={() => handleOpenZPdf(session.cash_session_uuid)}
                        >
                          PDF
                        </AdminButton>

                        <AdminButton size="small"
                          type="button"
                          onClick={() => openZEmailModal(session.cash_session_uuid)}
                        >
                          Envoyer PDF
                        </AdminButton>

                        <AdminButton size="small"
                          type="button"
                          onClick={() => handleSendZAccounting(session.cash_session_uuid)}
                        >
                          Compta
                        </AdminButton>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </ZHistoryTable>
            </ZHistoryTableWrapper>
          )}
        </AdminFormSection>
      )}

      {activeView === "tickets" && (
        <AdminFormSection>
          <AdminSectionHeader>
            <AdminSectionBadge>Tickets</AdminSectionBadge>
            <h4>Réimpression et correction</h4>
            <p>
              Vue dédiée aux tickets : recherche, réimpression, envoi email et modification moyen de paiement.
            </p>
          </AdminSectionHeader>

          <PlaceholderBox>
            Prochain lot : recherche ticket, réimpression, envoi email, correction moyen de paiement.
          </PlaceholderBox>
        </AdminFormSection>
      )}

      {selectedZReport && (
        <ModalOverlay onClick={() => setSelectedZReport(null)}>
          <ZModal onClick={(event) => event.stopPropagation()}>
            <ModalHeader>
              <div>
                <AdminSectionBadge>Détail Z</AdminSectionBadge>
                <h4>Rapport Z de clôture</h4>
                <p>
                  Session :{" "}
                  <CodeInline>
                    {selectedZReport.cash_session_uuid || selectedZReport.uuid || "—"}
                  </CodeInline>
                </p>
              </div>

              <CloseButton
                type="button"
                aria-label="Fermer le détail du rapport Z"
                onClick={() => setSelectedZReport(null)}
              >
                ×
              </CloseButton>
            </ModalHeader>

            <KPIGrid>
              <KPICard>
                <KPILabel>CA TTC</KPILabel>
                <KPIValue>{formatEUR(selectedZReport.summary?.total_ttc)}</KPIValue>
              </KPICard>

              <KPICard>
                <KPILabel>CA HT</KPILabel>
                <KPIValue>{formatEUR(selectedZReport.summary?.total_ht)}</KPIValue>
              </KPICard>

              <KPICard>
                <KPILabel>TVA</KPILabel>
                <KPIValue>
                  {formatEUR(
                    selectedZReport.summary?.total_vat ??
                      selectedZReport.summary?.vat
                  )}
                </KPIValue>
              </KPICard>

              <KPICard>
                <KPILabel>Tickets</KPILabel>
                <KPIValue>
                  {formatNumber(selectedZReport.summary?.tickets_count)}
                </KPIValue>
              </KPICard>

              <KPICard>
                <KPILabel>Panier moyen</KPILabel>
                <KPIValue>{formatEUR(selectedZReport.summary?.average_ticket)}</KPIValue>
              </KPICard>

              <KPICard>
                <KPILabel>Écart caisse</KPILabel>
                <KPIValue>
                  {formatEUR(selectedZReport.cash_control?.cash_difference)}
                </KPIValue>
              </KPICard>
            </KPIGrid>

            <ZSection>
              <h5>Contrôle de caisse</h5>
              <DetailGrid>
                <DetailItem>
                  <span>Fond de caisse</span>
                  <strong>{formatEUR(selectedZReport.cash_control?.opening_cash)}</strong>
                </DetailItem>

                <DetailItem>
                  <span>Espèces attendues</span>
                  <strong>{formatEUR(selectedZReport.cash_control?.expected_cash)}</strong>
                </DetailItem>

                <DetailItem>
                  <span>Espèces comptées</span>
                  <strong>{formatEUR(selectedZReport.cash_control?.counted_cash)}</strong>
                </DetailItem>

                <DetailItem>
                  <span>Dépôt banque</span>
                  <strong>{formatEUR(selectedZReport.cash_control?.bank_deposit)}</strong>
                </DetailItem>

                <DetailItem>
                  <span>Écart caisse</span>
                  <strong>{formatEUR(selectedZReport.cash_control?.cash_difference)}</strong>
                </DetailItem>
              </DetailGrid>
            </ZSection>

            <ZSection>
              <h5>Paiements</h5>
              <PaymentSection payments={selectedZReport.payments || {}} />
            </ZSection>

            <ZSection>
              <h5>Certification NF525</h5>
              <HashBlock>
                <span>Previous hash</span>
                <CodeCell>{selectedZReport.previous_hash || "—"}</CodeCell>

                <span>Signature hash</span>
                <CodeCell>{selectedZReport.signature_hash || "—"}</CodeCell>

                <span>Payload SHA-256</span>
                <CodeCell>{selectedZReport.payload_sha256 || "—"}</CodeCell>
              </HashBlock>
            </ZSection>

            <ZSection>
              <h5>Statuts de transmission</h5>

              <DetailGrid>
                <DetailItem>
                  <span>Email</span>
                  <strong>
                    {selectedZReport.email_sent ? "Envoyé" : "Non envoyé"}
                  </strong>
                </DetailItem>

                <DetailItem>
                  <span>Date email</span>
                  <strong>
                    {selectedZReport.email_sent_at
                      ? new Date(selectedZReport.email_sent_at).toLocaleString("fr-FR")
                      : "—"}
                  </strong>
                </DetailItem>

                <DetailItem>
                  <span>Comptabilité</span>
                  <strong>
                    {selectedZReport.accounting_sent ? "Transmis" : "Non transmis"}
                  </strong>
                </DetailItem>

                <DetailItem>
                  <span>Référence comptable</span>
                  <strong>{selectedZReport.accounting_reference || "—"}</strong>
                </DetailItem>
              </DetailGrid>
            </ZSection>

            <ModalActions>
              <AdminButton size="small"
                type="button"
                onClick={async () => {
                  const uuid = selectedZReport.cash_session_uuid || selectedZReport.uuid;
                  const refreshed = await getZReportDetail(uuid, {
                    companyUuid: selectedCompanyUuid,
                  });
                  setSelectedZReport(refreshed);
                }}
              >
                Actualiser
              </AdminButton>

              <AdminButton size="small"
                type="button"
                onClick={() =>
                  handleOpenZPdf(
                    selectedZReport.cash_session_uuid || selectedZReport.uuid
                  )
                }
              >
                PDF
              </AdminButton>

              <AdminButton size="small"
                type="button"
                onClick={() =>
                  openZEmailModal(
                    selectedZReport.cash_session_uuid || selectedZReport.uuid
                  )
                }
              >
                Envoyer PDF
              </AdminButton>

              <AdminButton size="small"
                type="button"
                onClick={() =>
                  handleSendZAccounting(
                    selectedZReport.cash_session_uuid || selectedZReport.uuid
                  )
                }
              >
                Compta
              </AdminButton>
            </ModalActions>
          </ZModal>
        </ModalOverlay>
      )}
      {emailModalSessionUuid && (
        <PdfEmailModal
          title="Envoyer le rapport Z"
          description="Le PDF Z sera généré et envoyé en pièce jointe."
          defaultTo={emailTo}
          defaultCc={emailCc}
          defaultSubject={emailSubject}
          defaultBody={emailBody}
          onClose={() => setEmailModalSessionUuid(null)}
          onSend={async ({ recipient, cc, subject, body }) => {
            setEmailTo(recipient);
            setEmailCc(cc || "");
            setEmailSubject(subject);
            setEmailBody(body);

            await sendZReportByEmail(
              emailModalSessionUuid,
              {
                recipient,
                cc,
                subject,
                body,
              },
              {
                companyUuid: selectedCompanyUuid,
              }
            );

            await loadZHistory();

            if (selectedZReport) {
              const refreshed = await getZReportDetail(emailModalSessionUuid, {
                companyUuid: selectedCompanyUuid,
              });
              setSelectedZReport(refreshed);
            }

            setLocalMessage("PDF Z envoyé par email avec succès.");
            setIsError(false);
          }}
        />
      )}
    </PanelWrapper>
  );
}

function PaymentSection({ payments }) {
  return (
    <AdminFormSection>
      <AdminSectionHeader>
        <AdminSectionBadge>Paiements</AdminSectionBadge>
        <h4>Répartition des paiements</h4>
      </AdminSectionHeader>

      <PaymentList>
        {Object.entries(paymentLabels).map(([key, label]) => (
          <PaymentRow key={key}>
            <span>{label}</span>
            <strong>{formatEUR(payments?.[key])}</strong>
          </PaymentRow>
        ))}
      </PaymentList>
    </AdminFormSection>
  );
}

function formatEUR(value) {
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
  }).format(Number(value || 0));
}

function formatNumber(value) {
  return new Intl.NumberFormat("fr-FR").format(Number(value || 0));
}

function formatDate(value) {
  if (!value) return "—";

  return new Date(value).toLocaleString("fr-FR");
}

const paymentLabels = {
  cash: "Espèces",
  card: "Carte bancaire",
  check: "Chèque",
  transfer: "Virement",
  crypto: "Crypto",
  giftCheck: "Chèque cadeau",
  titre_resto: "Titre restaurant",
  holidays_check: "Chèque vacances",
  restaurant_ticket: "Ticket restaurant",
  gift_cards: "Carte cadeau",
};

const PanelWrapper = styled.div`
  display: grid;
  gap: 0.95rem;
  color: #f7f7f7;
`;

const HeaderCard = styled.section`
  padding: 1rem;
  border-radius: 16px;
  background: #202a43;
  border: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: center;

  h4 {
    margin: 0.45rem 0 0;
    font-size: 1.15rem;
    font-weight: 950;
  }

  p {
    margin: 0.45rem 0 0;
    opacity: 0.78;
    font-size: 0.86rem;
    line-height: 1.45;
  }
`;

const CashState = styled.div`
  display: flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.55rem 0.85rem;
  border-radius: 999px;
  background: ${({ $open }) =>
    $open ? "rgba(34, 197, 94, 0.16)" : "rgba(239, 68, 68, 0.16)"};
  color: ${({ $open }) => ($open ? "#22c55e" : "#ef4444")};
  font-weight: 950;
  white-space: nowrap;

  span {
    width: 10px;
    height: 10px;
    border-radius: 999px;
    background: currentColor;
    box-shadow: 0 0 12px currentColor;
  }
`;

const ResultBlock = styled.div`
  margin-top: 1rem;
  display: grid;
  gap: 1rem;
`;

const KPIGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(165px, 1fr));
  gap: 0.75rem;
`;

const KPICard = styled.div`
  min-height: 92px;
  padding: 1rem;
  border-radius: 14px;
  background: #fff9ea;
  color: #0b1328;
  display: grid;
  align-content: space-between;
`;

const KPILabel = styled.div`
  font-size: 0.78rem;
  font-weight: 850;
  opacity: 0.76;
`;

const KPIValue = styled.div`
  margin-top: 0.7rem;
  font-size: 1.55rem;
  font-weight: 950;
  line-height: 1.1;
`;

const PaymentList = styled.div`
  display: grid;
  gap: 0.45rem;
`;

const PaymentRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.8rem 0.9rem;
  border-radius: 12px;
  background: rgba(255, 249, 234, 0.1);
  color: #fff9ea;

  span {
    font-size: 0.85rem;
    font-weight: 800;
  }

  strong {
    font-size: 0.9rem;
    font-weight: 950;
  }
`;

const ActionGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.75rem;
`;



const InlineInfo = styled.div`
  margin-top: 0.9rem;
  padding: 0.75rem 0.9rem;
  border-radius: 12px;
  background: rgba(255, 249, 234, 0.1);
  color: #fff9ea;
  font-size: 0.85rem;
  font-weight: 750;
  line-height: 1.4;
`;

const PanelMessage = styled.div`
  padding: 0.85rem 1rem;
  border-radius: 12px;
  font-weight: 850;
  background: ${({ $error }) =>
    $error ? "rgba(180, 40, 40, 0.9)" : "rgba(40, 140, 80, 0.9)"};
`;

const FieldsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.75rem;
  margin-bottom: 0.9rem;
`;

const Field = styled.div`
  display: grid;
  gap: 0.35rem;
  grid-column: ${({ $full }) => ($full ? "1 / -1" : "auto")};

  label {
    font-size: 0.78rem;
    font-weight: 850;
    opacity: 0.82;
  }

  input,
  textarea {
    width: 100%;
    min-height: 42px;
    border: none;
    border-radius: 10px;
    padding: 0.75rem;
    background: #fff9ea;
    color: #0b1328;
    font-weight: 750;
    box-sizing: border-box;
    outline: none;
  }

  input:focus {
    box-shadow: 0 0 0 3px rgba(255, 249, 234, 0.22);
  }

  textarea {
    resize: vertical;
    min-height: 150px;
  }
`;

const PlaceholderBox = styled.div`
  padding: 1rem;
  border-radius: 14px;
  background: rgba(255, 249, 234, 0.1);
  color: #fff9ea;
  font-weight: 800;
  line-height: 1.45;
`;

const ZHistoryTableWrapper = styled.div`
  margin-top: 1rem;
  overflow: auto;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.08);
`;

const ZHistoryTable = styled.table`
  width: 100%;
  min-width: 1180px;
  border-collapse: collapse;
  background: rgba(255, 255, 255, 0.04);

  th,
  td {
    padding: 0.65rem 0.75rem;
    text-align: left;
    font-size: 0.8rem;
    vertical-align: middle;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  }

  th {
    background: #0b1328;
    color: #fff9ea;
    font-weight: 950;
    position: sticky;
    top: 0;
    z-index: 1;
    white-space: nowrap;
  }

  td {
    color: #f7f7f7;
    white-space: nowrap;
  }
`;

const CodeCell = styled.code`
  display: block;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #fff9ea;
`;

const ActionMenu = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  flex-wrap: nowrap;
`;


const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 4rem 1rem;
  background: rgba(0, 0, 0, 0.72);
  overflow-y: auto;
`;

const ZModal = styled.div`
  width: min(980px, 100%);
  max-height: calc(100vh - 6rem);
  overflow-y: auto;
  padding: 1.25rem;
  border-radius: 22px;
  background: #1d263d;
  color: #f7f7f7;
  border: 1px solid rgba(255, 255, 255, 0.12);
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.5);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.2rem;

  h4 {
    margin: 0.35rem 0 0;
    font-size: 1.35rem;
  }

  p {
    margin: 0.35rem 0 0;
    opacity: 0.75;
  }
`;

const CloseButton = styled(AdminButton)`
  width: 38px;
  min-width: 38px;
  height: 38px;
  min-height: 38px;
  padding: 0;
  border: none;
  border-radius: 999px;
  background: #fff9ea;
  color: #0b1328;
  font-size: 1.6rem;
  font-weight: 900;
  line-height: 1;
`;

const ZSection = styled.section`
  margin-top: 1rem;
  padding: 1rem;
  border-radius: 16px;
  background: #202a43;
  border: 1px solid rgba(255, 255, 255, 0.08);

  h5 {
    margin: 0 0 0.8rem;
    font-size: 1rem;
  }
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 0.7rem;
`;

const DetailItem = styled.div`
  padding: 0.75rem;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.055);

  span {
    display: block;
    font-size: 0.75rem;
    opacity: 0.7;
  }

  strong {
    display: block;
    margin-top: 0.25rem;
    font-size: 1rem;
  }
`;

const HashBlock = styled.div`
  display: grid;
  gap: 0.45rem;

  span {
    font-size: 0.75rem;
    opacity: 0.7;
  }
`;

const CodeInline = styled.span`
  font-family: monospace;
  font-size: 0.8rem;
  opacity: 0.85;
`;

const ModalActions = styled.div`
  position: sticky;
  bottom: -1.25rem;
  display: flex;
  justify-content: flex-end;
  gap: 0.6rem;
  margin-top: 1rem;
  padding: 1rem 0 0.2rem;
  background: #1d263d;
`;